import { Link } from '@tanstack/react-router';
import { PLANS, type PlanTier } from '@aeon-karaoke/cloud-convex/model/plans';
import { Button, MonoLabel } from '@/component';
import { useBillingModals, useBillingSummary, type BillingSummary } from '@/hook';
import { formatUsd } from '@/lib/money';
import { formatRenewalDate } from '@/lib/time';

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** "1 song pack, 25 uploads a month." for a tier, from its inclusions and cap. */
export function planSentence(summary: BillingSummary, tier: PlanTier = summary.tier): string {
  const plan = PLANS[tier];
  const packs = plural(summary.packs.perPlan[tier], 'song pack');
  const uploads = plan.monthlyCreateLimit === null ? 'unlimited uploads' : `${plan.monthlyCreateLimit} uploads a month`;
  return `${packs}, ${uploads}.`;
}

/** "Renews Oct 4", or what is about to change instead. */
export function renewalLabel(summary: BillingSummary): string {
  if (summary.endsAt) return `Ends ${formatRenewalDate(summary.endsAt)}`;
  if (summary.pendingPlan) return `${PLANS[summary.pendingPlan.tier].name} from ${formatRenewalDate(summary.pendingPlan.startsAt)}`;
  if (summary.renewsAt) return `Renews ${formatRenewalDate(summary.renewsAt)}`;
  return summary.plan.name;
}

/** Home's plan card: the subscription in a sentence with Manage plan, or the
 * Beta offer for a free account. */
export function HomePlanCard() {
  const summary = useBillingSummary();
  const { openPlan, openCheckout } = useBillingModals();
  if (!summary) return null;

  if (summary.tier !== 'free') {
    return (
      <section className="rounded-md border border-line bg-surface p-[22px]">
        <MonoLabel tone="acc" size="md" className="block">
          {summary.plan.name} plan
        </MonoLabel>
        <p className="mt-[13px] text-[19px] font-medium leading-[1.35] tracking-[-0.02em]">{planSentence(summary)}</p>
        <div className="mt-[18px] border-t border-line pt-4">
          <MonoLabel tone="mute" size="md" className="tracking-[0.12em]">
            {renewalLabel(summary)}
          </MonoLabel>
        </div>
        <Button variant="outline" size="sm" className="mt-[15px] px-4" onClick={openPlan}>
          Manage plan
        </Button>
      </section>
    );
  }

  return (
    <section className="rounded-md border border-accFill bg-surface p-[22px]">
      <MonoLabel tone="acc" size="md" className="block">
        {PLANS.beta.name}
      </MonoLabel>
      <p className="mt-[13px] text-[19px] font-medium leading-[1.35] tracking-[-0.02em]">{planSentence(summary, 'beta')}</p>
      <p className="mt-[10px] text-sm leading-[1.5] text-mute">
        Up from {PLANS.free.monthlyCreateLimit} uploads a month on {PLANS.free.name}. Cancel any time.
      </p>
      <div className="mt-[18px] flex items-baseline gap-[6px] border-t border-line pt-4">
        <span className="text-[26px] font-semibold tracking-[-0.03em]">{formatUsd(PLANS.beta.priceCents.month)}</span>
        <span className="text-[13px] text-mute">a month</span>
      </div>
      {summary.checkout.open && (
        <Button size="sm" className="mt-[15px] px-[17px] py-[11px]" onClick={() => openCheckout({ kind: 'beta' })}>
          Upgrade
        </Button>
      )}
      <Link to="/settings/$tab" params={{ tab: 'plan' }} className="mt-[10px] block text-[13px] text-mute hover:text-ink">
        See plans
      </Link>
    </section>
  );
}
